/**
 * The conversion report's wire shape, mirrored from reports/conversion.ts on the server, plus the
 * pure arithmetic the section does with it.
 *
 * A pure module with NO imports, for the reason reportContract.ts gives: anything importing a hook
 * drags lib/firebase into the test runner. The comparison maths lives here so it can be asserted
 * without rendering a thing.
 */

export const CONVERSION_REPORT_ACTION = 'conversionReport';

export type ConversionStage =
  | 'lead' | 'meeting' | 'analysis' | 'proposal' | 'submission' | 'approval' | 'settlement';

export interface ConversionCell {
  from: ConversionStage;
  to: ConversionStage;
  denominator: number;
  converted: number;
  ratePct: number | null;
  lowSample: boolean;
}

export interface ConversionMatrix {
  cells: ConversionCell[];
  dealsInRates: number;
}

export interface ConversionBroker extends ConversionMatrix {
  adviserKey: string;
  adviserName: string;
}

export interface ConversionStageGroup {
  stage: ConversionStage;
  label: string;
  stageLabels: string[];
}

export interface ConversionReportOk {
  status: 'ok';
  firstAsAtDate: string;
  asAtDate: string;
  /** Snapshots actually read: at most snapshotCap. */
  snapshotCount: number;
  /** Published Performance snapshots that exist. Above snapshotCount when history was capped. */
  snapshotsAvailable: number;
  /** The most recent snapshots the server reads, at most (26: half a year of weekly uploads). */
  snapshotCap: number;
  historyDays: number;
  weeksOfHistory: number;
  longestGapDays: number;
  dealsFollowed: number;
  stageGroups: ConversionStageGroup[];
  ungroupedStageLabels: string[];
  business: ConversionMatrix;
  brokers: ConversionBroker[];
  lowSampleBelow: number;
}

export interface ConversionReportBuilding {
  status: 'building';
  snapshotCount: number;
  snapshotsAvailable: number;
  snapshotsNeeded: number;
  firstAsAtDate: string | null;
  asAtDate: string | null;
}

export type ConversionReportResult = ConversionReportOk | ConversionReportBuilding;

/** Row labels, as the design reads them: plural where the row is "deals at this stage". */
export const CONVERSION_ROWS: ReadonlyArray<{ stage: ConversionStage; label: string }> = [
  { stage: 'lead', label: 'Leads' },
  { stage: 'meeting', label: 'Meetings' },
  { stage: 'analysis', label: 'Analysis' },
  { stage: 'proposal', label: 'Proposals' },
  { stage: 'submission', label: 'Submissions' },
  { stage: 'approval', label: 'Approvals' },
];

export const CONVERSION_COLUMNS: ReadonlyArray<{ stage: ConversionStage; label: string }> = [
  { stage: 'meeting', label: 'Meeting' },
  { stage: 'analysis', label: 'Analysis' },
  { stage: 'proposal', label: 'Proposal' },
  { stage: 'submission', label: 'Submission' },
  { stage: 'approval', label: 'Approval' },
  { stage: 'settlement', label: 'Settlement' },
];

const ORDER: ConversionStage[] = ['lead', 'meeting', 'analysis', 'proposal', 'submission', 'approval', 'settlement'];

/** True when the column comes after the row, i.e. the cell is a real question. */
export const isForward = (from: ConversionStage, to: ConversionStage): boolean =>
  ORDER.indexOf(to) > ORDER.indexOf(from);

export function cellOf(matrix: ConversionMatrix, from: ConversionStage, to: ConversionStage): ConversionCell | null {
  return matrix.cells.find((c) => c.from === from && c.to === to) ?? null;
}

/**
 * A broker's gap to the business average, in percentage points, to one decimal place.
 *
 * Null when either side has no rate: a gap against nothing is not a gap, and printing one would
 * invite a coaching conversation about a number that does not exist.
 */
export function gapPoints(broker: ConversionCell | null, business: ConversionCell | null): number | null {
  if (!broker || !business || broker.ratePct === null || business.ratePct === null) return null;
  return Math.round((broker.ratePct - business.ratePct) * 10) / 10;
}

/** True when the server read fewer snapshots than exist because of its cap. */
export const conversionHistoryCapped = (result: ConversionReportOk): boolean =>
  result.snapshotsAvailable > result.snapshotCount;
